/**
 * Open enrollment badge for an account.
 *
 * Lives in common-web because the shared `AccountsListTable` renders it next to
 * the company name, alongside the client portal's kanban card and account
 * header — all three surfaces have to agree on when the window is worth showing
 * and what it says.
 *
 * An upcoming window gets a badge too, not just a live one: an admin who sets
 * the dates should see the account change immediately rather than wait for the
 * start date to arrive and wonder whether the window saved. Finished windows
 * drop off; the exact dates always stay on the Group Info tab.
 */

import type {ReactElement} from 'react';
import {CalendarClock} from 'lucide-react';
import {Badge} from '@common-web/components/ui/badge';
import {cn} from '@common-web/lib/utils';
import {formatDate} from '@common/lib/utils';
import {
  formatOpenEnrollmentRange,
  resolveOpenEnrollmentStatus,
  toOpenEnrollmentWindow,
  type OpenEnrollmentMetadata,
} from '@common/lib/openEnrollment';

interface OpenEnrollmentBadgeProps {
  /** The linked group's metadata row; null/undefined when there is no group. */
  metadata: OpenEnrollmentMetadata | null | undefined;
  /** Dense form for kanban cards and table rows — drops the date range. */
  compact?: boolean;
  className?: string;
}

export function OpenEnrollmentBadge({
  metadata,
  compact = false,
  className,
}: OpenEnrollmentBadgeProps): ReactElement | null {
  const enrollmentWindow = toOpenEnrollmentWindow(metadata);
  if (!enrollmentWindow) return null;

  const status = resolveOpenEnrollmentStatus(enrollmentWindow);
  if (status !== 'ACTIVE' && status !== 'UPCOMING') return null;

  const range = formatOpenEnrollmentRange(enrollmentWindow);
  const isActive = status === 'ACTIVE';
  const label = isActive
    ? compact
      ? 'Open Enrollment'
      : `Open Enrollment: ${range}`
    : compact
      ? `Open Enrollment ${formatDate(enrollmentWindow.startDate, 'MMM D')}`
      : `Open Enrollment opens ${formatDate(enrollmentWindow.startDate, 'MMM D, YYYY')}`;

  return (
    <Badge
      variant={isActive ? 'success' : 'info'}
      size={compact ? 'sm' : 'default'}
      className={cn('whitespace-nowrap', className)}
      // The compact form hides the dates, so keep them reachable on hover.
      title={range}
      icon={<CalendarClock className={compact ? 'h-2.5 w-2.5' : 'h-3 w-3'} />}
    >
      {label}
    </Badge>
  );
}
